import './TQServiceFilterSortOption.scss';

import { useEffect, useMemo, useState } from 'react';

import { Badge, Button, Checkbox, Radio } from 'antd';
import { BiFilterAlt } from 'react-icons/bi';
import { FaSort } from 'react-icons/fa6';
import { BottomSheet } from 'react-spring-bottom-sheet';

import Header from '../../../../components/Header/Header';
import { setServiceRequestsFilters } from '../../../../context/tqConnectServiceRequestReducer';
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks';
import { checkTQConnectAccess } from '../../../../shared/tqConnectUtils';
import {
  TQServiceRequestSortBy,
  TQServiceRequestSortOrder,
  TQServiceRequestStatusType,
  TQServiceRequestType,
  TQServiceRequestTypeValue,
} from '../../../../types/tqConnectTypes/service-requests';

export type SortOption =
  | 'help_high_low'
  | 'help_low_high'
  | 'date_newest'
  | 'date_oldest';

export type StatusFilter = 'open' | 'in_progress' | 'completed';

interface TQServiceFilterSortOptionProps {
  totalItems?: number;
  requestType: TQServiceRequestTypeValue;
}

const TQServiceFilterSortOption: React.FC<TQServiceFilterSortOptionProps> = ({
  totalItems,
  requestType,
}) => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [sortByState, setSortByState] = useState<
    TQServiceRequestSortBy | undefined
  >(undefined);
  const [sortOrderState, setSortOrderState] = useState<
    TQServiceRequestSortOrder | undefined
  >(undefined);
  const [statusesState, setStatusesState] = useState<
    TQServiceRequestStatusType[]
  >([]);

  const { userAccessType } = checkTQConnectAccess();

  const isSortFilter = useMemo(() => {
    return (
      requestType === TQServiceRequestType.DISCOVER &&
      userAccessType === 'customer'
    );
  }, [requestType, userAccessType]);

  const filterButtonDetails = useMemo(() => {
    if (!isSortFilter) {
      return { title: 'Filters', icon: <BiFilterAlt /> };
    }
    return { title: 'Sort by', icon: <FaSort /> };
  }, [isSortFilter]);

  const {
    serviceRequestsFilters: { sortBy, statuses, sortOrder },
  } = useAppSelector((state) => state.tqConnectServiceRequest);

  const handleApply = () => {
    dispatch(
      setServiceRequestsFilters({
        sortBy: sortByState,
        statuses: statusesState,
        sortOrder: sortOrderState,
      }),
    );
    setIsOpen(false);
  };

  const handleSortByChange = (value: string) => {
    const [newSortBy, newSortOrder] = value.split('_');
    setSortByState(newSortBy as TQServiceRequestSortBy);
    setSortOrderState(newSortOrder as TQServiceRequestSortOrder);
  };

  const resetFilters = () => {
    dispatch(
      setServiceRequestsFilters({
        sortBy: undefined,
        statuses: [],
        sortOrder: undefined,
      }),
    );
    setSortByState(undefined);
    setStatusesState([]);
    setSortOrderState(undefined);
  };

  const totalFilterCount = useMemo(
    () =>
      (statuses?.length || 0) +
      (sortBy !== TQServiceRequestSortBy.CREATED_AT && sortBy ? 1 : 0),
    [statuses, sortBy],
  );

  useEffect(() => {
    setSortByState(sortBy as TQServiceRequestSortBy);
    setStatusesState(statuses || ([] as TQServiceRequestStatusType[]));
    setSortOrderState(sortOrder as TQServiceRequestSortOrder);
  }, [sortBy, statuses, sortOrder]);

  useEffect(() => {
    if (isSortFilter) {
      handleApply();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSortFilter, sortByState]);

  return (
    <>
      <div className="service-requests-discover__filter">
        <div className="service-requests-discover__filter_request-count">
          {totalItems} requests
        </div>

        <Button
          className="service-requests-discover__filter_button"
          onClick={() => setIsOpen(true)}>
          <Badge
            count={totalFilterCount}
            overflowCount={99}
            style={{ marginRight: 8 }}
          />
          {filterButtonDetails.title}
          {filterButtonDetails.icon}
        </Button>
      </div>
      <BottomSheet
        open={isOpen}
        onDismiss={() => setIsOpen(false)}
        header={
          <Header
            title="Filters"
            canGoBack
            handleBack={() => setIsOpen(false)}
            backType="cross"
            noBorder
          />
        }>
        <div className="tq-filter-sort-sheet">
          <section className="tq-filter-sort-sheet__section">
            <h3 className="tq-filter-sort-sheet__section-title">SORT BY</h3>
            <Radio.Group
              value={`${sortByState}_${sortOrderState}`}
              onChange={(e) =>
                handleSortByChange(e.target.value as TQServiceRequestSortBy)
              }
              className="tq-filter-sort-sheet__radio-group">
              <Radio
                value={`${TQServiceRequestSortBy.TOTAL_RESPONSES}_${TQServiceRequestSortOrder.DESC}`}
                className="tq-filter-sort-sheet__radio">
                Help Offered: High to Low
              </Radio>
              <Radio
                value={`${TQServiceRequestSortBy.TOTAL_RESPONSES}_${TQServiceRequestSortOrder.ASC}`}
                className="tq-filter-sort-sheet__radio">
                Help Offered: Low to High
              </Radio>
              <Radio
                value={`${TQServiceRequestSortBy.CREATED_AT}_${TQServiceRequestSortOrder.DESC}`}
                className="tq-filter-sort-sheet__radio">
                Date: Newest First
              </Radio>
              <Radio
                value={`${TQServiceRequestSortBy.CREATED_AT}_${TQServiceRequestSortOrder.ASC}`}
                className="tq-filter-sort-sheet__radio">
                Date: Oldest First
              </Radio>
            </Radio.Group>
          </section>

          {!isSortFilter ? (
            <section className="tq-filter-sort-sheet__section">
              <h3 className="tq-filter-sort-sheet__section-title">STATUS</h3>
              <div className="tq-filter-sort-sheet__checkbox-group">
                <Checkbox
                  checked={statusesState.includes('open')}
                  onChange={(e) =>
                    setStatusesState((prev) =>
                      e.target.checked
                        ? ['open', ...prev]
                        : prev.filter((status) => status !== 'open'),
                    )
                  }
                  className="tq-filter-sort-sheet__checkbox">
                  Open
                </Checkbox>
                <Checkbox
                  checked={statusesState.includes('in-progress')}
                  onChange={(e) =>
                    setStatusesState((prev) =>
                      e.target.checked
                        ? ['in-progress', ...prev]
                        : prev.filter((status) => status !== 'in-progress'),
                    )
                  }
                  className="tq-filter-sort-sheet__checkbox">
                  In Progress
                </Checkbox>
                <Checkbox
                  checked={statusesState.includes('completed')}
                  onChange={(e) =>
                    setStatusesState((prev) =>
                      e.target.checked
                        ? ['completed', ...prev]
                        : prev.filter((status) => status !== 'completed'),
                    )
                  }
                  className="tq-filter-sort-sheet__checkbox">
                  Completed
                </Checkbox>
                <Checkbox
                  checked={statusesState.includes('rejected')}
                  onChange={(e) =>
                    setStatusesState((prev) =>
                      e.target.checked
                        ? ['rejected', ...prev]
                        : prev.filter((status) => status !== 'rejected'),
                    )
                  }
                  className="tq-filter-sort-sheet__checkbox">
                  Rejected
                </Checkbox>
              </div>
            </section>
          ) : null}

          <div className="tq-filter-sort-sheet__actions">
            {!isSortFilter ? (
              <Button
                type="primary"
                className="tq-filter-sort-sheet__apply-btn"
                block
                onClick={handleApply}>
                Apply
              </Button>
            ) : null}
            <button
              type="button"
              className="tq-filter-sort-sheet__clear-btn"
              onClick={() => {
                resetFilters();
              }}>
              Clear Filter
            </button>
          </div>
        </div>
      </BottomSheet>
    </>
  );
};

export default TQServiceFilterSortOption;
